const i18nStrings = {
    appName: 'Workshop Studio',
    and: 'and',
    close: 'Close',
    closeWindow: 'Close window',
    emptyValue: '-',
    errors: {
        notFound: {
            title: 'Page not found',
            message:
                'The page you are looking for does not exist. You might have typed the address incorrectly or you might have used an outdated link. Please check the link and try again.',
        },
    },
    signIn: {
        title: 'Sign in',
        description: 'Choose a preferred sign-in method',
        signingIn: 'Signing in...',
        providers: {
            email: {
                label: 'Email one-time password (OTP)',
                description:
                    'Enter your personal or corporate email to receive a one-time password',
            },
            amazonRetail: {
                label: 'Login with Amazon',
                description: 'Login with your Amazon.com retail account',
            },
            amazonInternal: {
                label: 'Amazon employee',
                description:
                    'Login with your Amazon Corporate account. Only for Amazon Employees.',
            },
        },
        callback: {
            signingIn: 'Completing sign-in...',
            signInFailure:
                'Failed to sign in at this time. Please try clearing cookies and refreshing the page. If the problem persists, please try refreshing the page at a later time.',
        },
    },
    event: {
        headers: {
            dashboard: 'Event dashboard',
            eventInformation: 'Event information',
            workshop: 'Workshop',
            sshKey: {
                title: 'EC2 SSH key',
                name: 'Key name',
                fingerprint: 'Fingerprint',
                privateKey: 'Private key',
            },
        },
        downloadKeyPair: 'Download key pair',
        openAWSConsole: 'Open AWS console',
        getAWSCliCredentials: 'Get AWS CLI credentials',
        awsCredentialsInfoMessage:
            'Only use {0} as your region, unless otherwise directed by the event operator. You can optionally set the `AWS_DEFAULT_REGION` environment variable to one of the allowed regions to set the default region for CLI commands. This value can be overridden with the `--region` command line parameter.',
        getEC2SshKey: 'Get EC2 SSH key',
        aboutEnvironmentVariables: {
            p1:
                'These environment variables need to be set in your terminal session to run AWS CLI commands against your temporary AWS account. Learn more about the AWS CLI in the',
            cta: 'official user guide',
        },
        startWorkshop: 'Get started',
        exit: 'Exit event',
        loadingDashboard: 'Loading event dashboard...',
        loadingContent: 'Loading event content...',
        labels: {
            startTime: 'Start time',
            duration: 'Duration',
            description: 'Description',
            accessibleRegions: 'Accessible regions',
            title: 'Title',
            complexityLevel: 'Complexity level',
            AWSServices: 'AWS services',
            topics: 'Topics',
        },
        sections: {
            AWSAccountAccess: 'AWS account access',
        },
        errors: {},
        modals: {
            eventEnded: {
                header: 'Event ended',
                content: 'The event is now over. Thank you for attending!',
                actionPrimary: 'Exit event',
            },
        },
        notifications: {
            eventNotStarted: {
                header: 'Event not started',
                content: 'Please wait until your facilitator starts the event.',
            },
            eventInProgress: {
                content: 'Event ends in {0}.',
            },
            eventEnded: {
                content: 'Event ended.',
            },
            eventPaused: {
                header: 'Event paused',
                content:
                    'Please wait until your facilitator resumes the event.',
            },
            noCurrentParticipant: {
                content:
                    'You are currently not participating in any active event.',
            },
        },
        eventOutputsTable: {
            title: 'Event Outputs',
            errorTitle: 'Problem getting stack outputs',
            emptyTitle: 'No stack outputs',
            emptyDescription: 'No stack outputs to display.',
            columns: {
                key: 'Key',
                value: 'Value',
                description: 'Description',
                source: 'Source',
                stackName: 'Stack name',
                type: 'Type',
            },
            propertyFilter: {
                key: 'Key values',
                stackName: 'Stack names',
                value: 'Values',
                description: 'Descriptions',
                source: 'Source name values',
                type: 'Types',
            },
        },
    },
    loading: 'Loading...',
    pageSize: 'Page size',
    nextPage: 'Next page',
    previousPage: 'Previous page',
    pageLabel: (pageNumber: number) => `Page ${pageNumber} of all pages`,
    wrapLines: 'Wrap lines',
    wrapLinesDescription: 'Check to see all the text and wrap the lines',
    confirm: 'Confirm',
    cancel: 'Cancel',
    preferences: 'Preferences',
    table: {
        noMatches: 'No matches',
        noMatchesDescription: 'We can not find a match.',
        clearFilters: 'Clear filters',
        clearAllFilters: 'Clear all filters',
        selectVisibleColumns: 'Select visible columns',
        properties: 'Properties',
        placeholder: 'Search by keyword',
        operationAnd: 'and',
        operationNotAnd: 'and not',
        operationOr: 'or',
        operationNotOr: 'or not',
        groupValues: 'Values',
        filteringEmpty: 'No items found',
        filtering: 'Filter items',
    },
    item: {
        singular: 'item',
        plural: 'items',
    },
    match: {
        singular: 'match',
        plural: 'matches',
    },
    termsAndConditions:
        'Read and accept before joining the event:\n1. By using AWS Workshop Studio for the relevant event, You agree to [the AWS Event Terms and Conditions](https://aws.amazon.com/events/terms) and the [AWS Acceptable Use Policy](https://aws.amazon.com/aup).\n1. If You are under 18 years old, you may participate in the relevant event using AWS Workshop Studio: (a) if You are at least the minimum age below based on the country or region in which You reside, and (b) with the involvement of a parent, guardian, or educator.\n\n   |Country or region|Minimum age|\n   |:-|-:|\n   |All countries or regions not listed below (including the United States, Brazil, the United Kingdom, and India)|13|\n   |Canada, China, Republic of Korea (South Korea)|14|\n   |Australia|15|\n   |Japan, Switzerland, Vietnam, and countries in the European Economic Area|16|\n   |Azerbaijan, Bolivia, Colombia, Dominican Republic, Indonesia, Lebanon, Malaysia, Mexico, Montenegro, Nepal, Philippines, Thailand, Turkey, and countries in Africa|18|\n\n1. You acknowledge and agree that You are using an AWS-owned account that You will only be able to access during the relevant event. You have no ownership rights over this AWS-owned account.\n1. During the relevant event, while using this AWS-owned account, You will not use, import, input, or introduce any data, dataset, or other material that contains personal data, financial information, or any other data or materials that may be subject to laws and regulations (such as the General Data Protection Regulation or The Health Insurance Portability and Accountability Act of 1996).\n1. If You find residual resources or materials in this AWS-owned account, You will notify your Event Operator immediately.\n1. AWS, its affiliates, and any entities or persons acting on AWS’s behalf reserves the right to terminate this AWS-owned account and to delete its contents at any time, without any notice to You.\n1. During the relevant event, while using this AWS-owned account, You will not process or run any operation on any data other than test datasets or lab materials that have been approved by AWS.\n1. You will not copy, import, export or otherwise create derivative works of materials provided by AWS for use outside of the relevant event.\n1. AWS, its affiliates, and any entities or persons acting on AWS’s behalf have no obligation to enable the transmission of Your materials through AWS Workshop Studio, and may, in their discretion, edit, block, refuse to post, or remove Your materials at any time, without notice to You.\n1. If You access and use a service and/or third-party models that have their own terms during the relevant event, while in the AWS-owned account, You agree to review those terms and comply with them during the event.\n1. If You are an AWS Partner using AWS Workshop Studio as part of Your participation in the AWS Partner Network Program, Your use of AWS Workshop Studio is governed by these terms, the AWS Partner Network Terms and Conditions, and the AWS Customer Agreement or other agreement with us governing your use of AWS Services.\n1. Your use of AWS Workshop Studio will comply with these terms and all applicable laws. If You fail to comply with any of these terms, Your access to AWS Workshop Studio may be immediately terminated, without notice to You.',
};

export default i18nStrings;
